import { useEffect, useRef, useState, useCallback } from 'react';
import { throttle } from 'lodash';

interface UseScreenSlideInOptions {
  triggerSlideRatio?: number;
  animationClassName?: string;
  onTrigger?: (element: HTMLElement) => void;
  scrollContainerSelector?: string | null;
  /** 为 true 时只触发一次，滚出视口后不再重置，也不会移除动画类名 */
  triggerOnce?: boolean;
  /** 为 true 时跳过挂载时的初始检查，仅在实际滚动时当元素进入触发区才执行（避免一进页面就播完） */
  skipInitialCheck?: boolean;
}

export function useScreenSlideIn(options: UseScreenSlideInOptions = {}) {
  const {
    triggerSlideRatio = 0.95,
    animationClassName,
    onTrigger,
    scrollContainerSelector: scrollContainerSelectorOption,
    triggerOnce = false,
    skipInitialCheck = false,
  } = options;

  const scrollContainerSelector =
    scrollContainerSelectorOption === undefined ? '.aixue-new-home' : scrollContainerSelectorOption;
  const rootRef = useRef<HTMLElement | null>(null);
  const lastTopRef = useRef<number | null>(null);
  const hasAnimatedRef = useRef<boolean>(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const [screenTopDistance, setScreenTopDistance] = useState<number>(0);

  // 重置动画状态
  const resetAnimation = useCallback(() => {
    hasAnimatedRef.current = false;
  }, []);

  const triggerAnimation = useCallback(
    (rootEl: HTMLElement) => {
      if (animationClassName) {
        rootEl.classList.remove(animationClassName);
        void rootEl.offsetHeight;
        requestAnimationFrame(() => {
          rootEl.classList.add(animationClassName);
        });
        hasAnimatedRef.current = true;
      }
      if (onTrigger) {
        onTrigger(rootEl);
        hasAnimatedRef.current = true;
      }
    },
    [animationClassName, onTrigger],
  );

  /** scrollTarget 为自定义容器时，以容器可见区域为基准计算触发线，更及时 */
  const getTriggerInfo = useCallback(
    (rootEl: HTMLElement, scrollTarget: HTMLElement | Window) => {
      const rect = rootEl.getBoundingClientRect();
      const currentTop = rect.top;
      const previousTop = lastTopRef.current;

      let triggerLine: number;
      let visibleTop: number;
      let visibleBottom: number;

      if (scrollTarget instanceof Window) {
        visibleTop = 0;
        visibleBottom = window.innerHeight;
        triggerLine = visibleBottom * triggerSlideRatio;
      } else {
        const containerRect = scrollTarget.getBoundingClientRect();
        visibleTop = containerRect.top;
        visibleBottom = containerRect.bottom;
        const containerHeight = containerRect.height;
        // ratio=1 表示元素顶端刚到容器底边即触发；>1 表示提前触发（更及时）
        triggerLine = visibleTop + containerHeight * triggerSlideRatio;
      }

      const scrollingDown = previousTop !== null && currentTop < previousTop;
      const isInTriggerZone = rect.top <= triggerLine && rect.bottom > visibleTop;
      const isFullyOutOfView = rect.bottom < visibleTop || rect.top > visibleBottom;
      const hasLeftAtBottom = rect.top > visibleBottom;

      lastTopRef.current = currentTop;
      return { rect, scrollingDown, isInTriggerZone, isFullyOutOfView, hasLeftAtBottom };
    },
    [triggerSlideRatio],
  );

  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const previousTop = lastTopRef.current;
      const { rect, scrollingDown, isInTriggerZone, isFullyOutOfView, hasLeftAtBottom } =
        getTriggerInfo(rootEl, scrollTarget);

      setScrollDirection(scrollingDown ? 'down' : 'up');
      setScreenTopDistance(rect.top);

      const shouldTrigger =
        isInTriggerZone &&
        !hasAnimatedRef.current &&
        (scrollingDown || (previousTop === null && !skipInitialCheck));
      if (shouldTrigger && (animationClassName || onTrigger)) {
        triggerAnimation(rootEl);
      } else if (isFullyOutOfView && !triggerOnce) {
        hasAnimatedRef.current = false;
      }
      if (hasLeftAtBottom && animationClassName && !triggerOnce) {
        rootEl.classList.remove(animationClassName);
      }
    };

    let scrollTarget: HTMLElement | Window = window;
    if (scrollContainerSelector != null && scrollContainerSelector !== '') {
      const containerElement = document.querySelector(
        scrollContainerSelector,
      ) as HTMLElement | null;
      if (containerElement) {
        const computedStyle = window.getComputedStyle(containerElement);
        const hasOverflow =
          computedStyle.overflowY === 'auto' ||
          computedStyle.overflowY === 'scroll' ||
          computedStyle.overflow === 'auto' ||
          computedStyle.overflow === 'scroll';

        if (hasOverflow && containerElement.scrollHeight > containerElement.clientHeight) {
          scrollTarget = containerElement;
        }
      }
    }

    const throttledHandleScroll = throttle(handleScroll, 100);

    scrollTarget.addEventListener('scroll', throttledHandleScroll, { passive: true });

    // 初始化时检查是否已经在触发区域（与 handleScroll 使用同一套基准）
    const rootEl = rootRef.current;
    if (rootEl && !skipInitialCheck) {
      const { isInTriggerZone } = getTriggerInfo(rootEl, scrollTarget);
      if (isInTriggerZone && (animationClassName || onTrigger)) {
        triggerAnimation(rootEl);
      }
    }

    return () => {
      scrollTarget.removeEventListener('scroll', throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [
    scrollContainerSelector,
    triggerAnimation,
    getTriggerInfo,
    animationClassName,
    triggerSlideRatio,
    onTrigger,
    triggerOnce,
    skipInitialCheck,
  ]);

  return {
    ref: rootRef,
    scrollDirection,
    screenTopDistance,
    reset: resetAnimation,
  };
}
